import { useCallback, useEffect, useState } from 'react'
import AtlassianSvg from '~/assets/atlassian.svg'
import AzureSvg from '~/assets/azure.svg'
import BoomiSvg from '~/assets/boomi.png'
import MulesoftPng from '~/assets/mulesoft.png'
import { AnypointExchangeItemModal } from './components/AnypointExchangeItemModal'
import { Button } from '~/components/common/Button'
import { InputArea } from '~/components/common/InputArea'
import { Modal } from '~/components/modals/CurieModal'
import { SettingItem } from '~/components/common/SettingItem'
import { Typography } from '~/components/common/Typography'
import { AccountType } from '~/constants/settings'
import { useBillingContext } from '~/contexts/BillingContext'
import { useUserContext } from '~/contexts/UserContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { BetterToast } from '~/utils/toast'

/** Small "Beta" pill, matching the skills list (SkillsView). */
const BetaPill = () => (
  <Typography
    fontFamily='geistMono'
    fontSize='labelSm'
    fontWeight='semibold'
    className='shrink-0 rounded-[6px] bg-tokens-primary-4 px-1.5 py-0.5 text-tokens-neutral-bg-white select-none'
  >
    Beta
  </Typography>
)

interface ExchangeItemProps {
  label: string
  image: string
  connected: boolean
  metadata: unknown
  beta?: boolean
  refreshIntegrations: () => void
  IntegrationItemModal?: (props: IntegrationItemModalProps) => React.ReactNode
}

function IntegrationItem({
  label,
  image,
  connected,
  beta,
  refreshIntegrations,
  IntegrationItemModal,
  metadata
}: ExchangeItemProps) {
  const [open, setOpen] = useState<boolean>(false)

  const onClose = () => {
    setOpen(false)
  }

  return (
    <div className='w-full'>
      <SettingItem
        variant='card'
        isConnected={connected}
        title={
          <>
            <div className='w-8 h-8 flex items-center justify-center'>
              <img
                src={image}
                alt={label}
                className='select-none pointer-events-none'
                draggable={false}
              />
            </div>
            <span className='text-[15px] font-semibold text-[rgba(46,31,54,0.90)] leading-normal font-mono select-none'>
              {label}
            </span>
            {beta && <BetaPill />}
          </>
        }
        onClick={() => setOpen(true)}
      >
        <div className={`p-2 rounded-lg pointer-events-none`}>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
          >
            <g clipPath='url(#clip0_203_17389)'>
              <path
                d='M11.3537 8.52035L6.35372 13.5203C6.30727 13.5668 6.25212 13.6037 6.19142 13.6288C6.13072 13.6539 6.06567 13.6669 5.99997 13.6669C5.93428 13.6669 5.86922 13.6539 5.80852 13.6288C5.74783 13.6037 5.69268 13.5668 5.64622 13.5203C5.59977 13.4739 5.56292 13.4187 5.53778 13.358C5.51263 13.2974 5.49969 13.2323 5.49969 13.1666C5.49969 13.1009 5.51263 13.0358 5.53778 12.9752C5.56292 12.9145 5.59977 12.8593 5.64622 12.8128L10.2931 8.1666L5.64622 3.52035C5.5524 3.42653 5.49969 3.29928 5.49969 3.1666C5.49969 3.03392 5.5524 2.90667 5.64622 2.81285C5.74004 2.71903 5.86729 2.66632 5.99997 2.66632C6.13265 2.66632 6.2599 2.71903 6.35372 2.81285L11.3537 7.81285C11.4002 7.85928 11.4371 7.91443 11.4623 7.97513C11.4874 8.03583 11.5004 8.10089 11.5004 8.1666C11.5004 8.23231 11.4874 8.29737 11.4623 8.35807C11.4371 8.41877 11.4002 8.47391 11.3537 8.52035Z'
                fill='black'
                fillOpacity='0.88'
              />
            </g>
            <defs>
              <clipPath id='clip0_203_17389'>
                <rect width='16' height='16' fill='white' transform='translate(0 0.166626)' />
              </clipPath>
            </defs>
          </svg>
        </div>
      </SettingItem>
      {IntegrationItemModal ? (
        <IntegrationItemModal
          open={open}
          onClose={onClose}
          refreshIntegrations={refreshIntegrations}
          metadata={metadata}
          isEditing={connected}
        />
      ) : null}
    </div>
  )
}

interface IntegrationItemModalProps {
  open: boolean
  onClose: () => void
  refreshIntegrations: () => void
  metadata: unknown
  isEditing: boolean
}

/**
 * Reduce a Jira URL to its origin (e.g. https://your-domain.atlassian.net).
 *
 * Atlassian Cloud serves the Jira REST API from the bare site origin, so any
 * path the user pastes (a trailing "/jira/", a full board URL, etc.) must be
 * stripped — otherwise the backend client fails to initialize. The backend
 * normalizes too; this mirrors it so the field visibly autocorrects on blur.
 *
 * Jira-specific on purpose: Confluence Cloud needs its "/wiki" context path, so
 * do NOT reuse this for the Confluence modal.
 */
function normalizeJiraUrl(raw: string): string {
  const trimmed = raw.trim()
  try {
    const parsed = new URL(trimmed)
    return `${parsed.protocol}//${parsed.host}`
  } catch {
    return trimmed.replace(/\/+$/, '')
  }
}

function JiraItemModal({
  open,
  onClose,
  refreshIntegrations,
  metadata,
  isEditing
}: IntegrationItemModalProps) {
  const { currentWorkspace } = useWorkspaceContext()
  const [email, setEmail] = useState<string>('')
  const [url, setUrl] = useState<string>('')
  const [token, setToken] = useState<string>('')
  const [tested, setTested] = useState<boolean>(false)
  const [isTestingConnection, setIsTestingConnection] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const jiraMetadata = metadata as {
    email: string
    url: string
    token: string
  } | null

  useEffect(() => {
    if (open && jiraMetadata) {
      setEmail(jiraMetadata.email || '')
      setUrl(jiraMetadata.url || '')
      setToken(jiraMetadata.token || '')
      if (jiraMetadata.email && jiraMetadata.url && jiraMetadata.token) {
        setTested(true)
      } else {
        setTested(false)
      }
      setErrorMessage('')
    }
  }, [open, jiraMetadata])

  const handleInputChange = (field: 'email' | 'url' | 'token', value: string) => {
    if (field === 'email') setEmail(value)
    else if (field === 'url') setUrl(value)
    else setToken(value)

    if (errorMessage || tested) {
      setErrorMessage('')
      setTested(false)
    }
  }

  // Autocorrect the URL field on blur so the user sees the cleaned origin before
  // testing/saving (see normalizeJiraUrl).
  const handleUrlBlur = () => {
    const normalized = normalizeJiraUrl(url)
    if (normalized !== url) setUrl(normalized)
  }

  const handleTestConnection = async () => {
    if (!email || !url || !token) {
      setErrorMessage('Please enter Email, URL and Token')
      return
    }

    if (!currentWorkspace?.uuid) {
      setErrorMessage('Workspace not found')
      return
    }

    setIsTestingConnection(true)
    setErrorMessage('')
    try {
      const data = await fetchWrapper.post<{ success: boolean; message: string }>(
        `/api/workspace/${currentWorkspace.uuid}/integration/jira_credentials/test`,
        {
          email,
          url,
          token
        },
        { credentials: 'include' }
      )

      if (data.success) {
        setTested(true)
        setErrorMessage('')
      } else {
        setTested(false)
        setErrorMessage(data.message || 'Connection failed. Please retry.')
      }
    } catch (error) {
      setTested(false)
      setErrorMessage('Connection failed. Please retry.')
      console.error('Test connection error:', error)
    } finally {
      setIsTestingConnection(false)
    }
  }

  const handleSaveConfiguration = async () => {
    if (!currentWorkspace?.uuid) {
      BetterToast.error('Workspace not found')
      return
    }

    setIsSaving(true)
    try {
      const method = isEditing ? 'put' : 'post'
      const data = await fetchWrapper[method]<{ success: boolean; message: string }>(
        `/api/workspace/${currentWorkspace.uuid}/integration/jira_credentials`,
        {
          email,
          url,
          token
        },
        { credentials: 'include' }
      )

      if (data.success) {
        BetterToast.success(
          isEditing ? 'Integration updated successfully!' : 'Integration saved successfully!'
        )
        refreshIntegrations()
        onClose()
      } else {
        BetterToast.error(data.message || `Failed to ${isEditing ? 'update' : 'save'} integration`)
      }
    } catch (error) {
      BetterToast.error(`Failed to ${isEditing ? 'update' : 'save'} integration. Please try again.`)
      console.error('Save integration error:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const disabled = isTestingConnection || !email || !url || !token

  return (
    <Modal className='w-[600px]' isOpen={open} onClose={onClose} hideCloseButton={false}>
      <div className='p-4 flex gap-x-4 items-center border-b border-neutral-border-2'>
        <img
          className='w-8 h-8 select-none pointer-events-none'
          src={AtlassianSvg}
          alt='Jira'
          draggable={false}
        />
        <Typography fontFamily='geist' fontSize='titleSm' fontWeight='bold' className='select-none'>
          Jira
        </Typography>
      </div>
      <div className='p-12'>
        <div className='w-[360px] mx-auto flex flex-col gap-y-4'>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Email
            </Typography>
            <InputArea
              value={email}
              onChange={e => handleInputChange('email', e.target.value)}
              fullWidth
              singleLine
              placeholder='user@example.com'
            />
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Jira URL
            </Typography>
            <InputArea
              value={url}
              onChange={e => handleInputChange('url', e.target.value)}
              onBlur={handleUrlBlur}
              fullWidth
              singleLine
              placeholder='https://your-domain.atlassian.net'
            />
            <Typography
              fontFamily='geist'
              fontSize='labelSm'
              className='text-tokens-neutral-text-tertiary select-none'
            >
              e.g. https://your-domain.atlassian.net
            </Typography>
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              API Token
            </Typography>
            <InputArea
              value={token}
              onChange={e => handleInputChange('token', e.target.value)}
              fullWidth
              singleLine
              type='password'
            />
          </div>
          <div className='flex items-center justify-end gap-x-2'>
            {errorMessage && (
              <Typography
                fontFamily='geistMono'
                fontSize='labelSm'
                className='text-tokens-status-error-text'
                fontWeight='semibold'
              >
                {errorMessage}
              </Typography>
            )}
            <Button
              variant='borderless'
              className={(() => {
                if (tested) return 'bg-tokens-neutral-fill-fill-6'
                return undefined
              })()}
              onClick={handleTestConnection}
              isDisabled={disabled}
            >
              <div className='flex gap-x-2 items-center select-none'>
                {isTestingConnection ? (
                  <>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      className='animate-spin'
                    >
                      <path
                        d='M8 1.5C6.71442 1.5 5.45772 1.88122 4.3888 2.59545C3.31988 3.30968 2.48676 4.32484 1.99479 5.51256C1.50282 6.70028 1.37409 8.00721 1.6249 9.26809C1.8757 10.529 2.49477 11.6872 3.40381 12.5962C4.31285 13.5052 5.47104 14.1243 6.73192 14.3751C7.99279 14.6259 9.29973 14.4972 10.4874 14.0052C11.6752 13.5132 12.6903 12.6801 13.4046 11.6112C14.1188 10.5423 14.5 9.28558 14.5 8'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                      />
                    </svg>
                    <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                      Testing...
                    </Typography>
                  </>
                ) : tested ? (
                  <>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M7.99961 14.4C11.5342 14.4 14.3996 11.5346 14.3996 8.00001C14.3996 4.46538 11.5342 1.60001 7.99961 1.60001C4.46499 1.60001 1.59961 4.46538 1.59961 8.00001C1.59961 11.5346 4.46499 14.4 7.99961 14.4ZM10.9653 6.96569C11.2777 6.65327 11.2777 6.14674 10.9653 5.83432C10.6529 5.5219 10.1463 5.5219 9.83392 5.83432L7.19961 8.46863L6.16529 7.43432C5.85288 7.1219 5.34634 7.1219 5.03392 7.43432C4.7215 7.74674 4.7215 8.25327 5.03392 8.56569L6.63392 10.1657C6.94634 10.4781 7.45288 10.4781 7.76529 10.1657L10.9653 6.96569Z'
                        fill='#2C8450'
                      />
                    </svg>
                    <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                      Test
                    </Typography>
                  </>
                ) : errorMessage ? (
                  <>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M7.99967 14.5953C4.31767 14.5953 1.33301 11.6107 1.33301 7.92866C1.33301 4.24733 4.31767 1.26199 7.99967 1.26199C11.6817 1.26199 14.6663 4.24733 14.6663 7.92866C14.6663 11.6107 11.6817 14.5953 7.99967 14.5953ZM7.99967 4.59533C7.82286 4.59533 7.65329 4.66556 7.52827 4.79059C7.40325 4.91561 7.33301 5.08518 7.33301 5.26199V8.59533C7.33301 8.77214 7.40325 8.94171 7.52827 9.06673C7.65329 9.19176 7.82286 9.26199 7.99967 9.26199C8.17649 9.26199 8.34605 9.19176 8.47108 9.06673C8.5961 8.94171 8.66634 8.77214 8.66634 8.59533V5.26199C8.66634 5.08518 8.5961 4.91561 8.47108 4.79059C8.34605 4.66556 8.17649 4.59533 7.99967 4.59533ZM7.99967 11.262C8.17649 11.262 8.34605 11.1918 8.47108 11.0667C8.5961 10.9417 8.66634 10.7721 8.66634 10.5953C8.66634 10.4185 8.5961 10.2489 8.47108 10.1239C8.34605 9.9989 8.17649 9.92866 7.99967 9.92866C7.82286 9.92866 7.65329 9.9989 7.52827 10.1239C7.40325 10.2489 7.33301 10.4185 7.33301 10.5953C7.33301 10.7721 7.40325 10.9417 7.52827 11.0667C7.65329 11.1918 7.82286 11.262 7.99967 11.262V11.262Z'
                        fill='#D94B36'
                      />
                    </svg>
                    <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                      Test Again
                    </Typography>
                  </>
                ) : (
                  <>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M14.5 3C14.6326 3 14.7598 3.05268 14.8536 3.14645C14.9473 3.24021 15 3.36739 15 3.5V12.5C15 12.6326 14.9473 12.7598 14.8536 12.8536C14.7598 12.9473 14.6326 13 14.5 13H1.5C1.36739 13 1.24021 12.9473 1.14645 12.8536C1.05268 12.7598 1 12.6326 1 12.5V3.5C1 3.36739 1.05268 3.24021 1.14645 3.14645C1.24021 3.05268 1.36739 3 1.5 3H14.5ZM1.5 2C1.10218 2 0.720644 2.15804 0.43934 2.43934C0.158035 2.72064 0 3.10218 0 3.5L0 12.5C0 12.8978 0.158035 13.2794 0.43934 13.5607C0.720644 13.842 1.10218 14 1.5 14H14.5C14.8978 14 15.2794 13.842 15.5607 13.5607C15.842 13.2794 16 12.8978 16 12.5V3.5C16 3.10218 15.842 2.72064 15.5607 2.43934C15.2794 2.15804 14.8978 2 14.5 2H1.5Z'
                        fill='#262626'
                        fillOpacity={disabled ? '0.2' : '1'}
                      />
                    </svg>
                    <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                      Test Connection
                    </Typography>
                  </>
                )}
              </div>
            </Button>
          </div>
        </div>
      </div>
      <div className='py-4 px-8 flex gap-2 bg-tokens-neutral-bg-grey-1 border-t border-tokens-neutral-border-2 justify-end items-center select-none'>
        <div className='flex gap-2'>
          <Button
            variant='outlined'
            onClick={onClose}
            className='text-[#262626] border-[#D6D6D6] bg-white hover:bg-gray-50'
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            isDisabled={!tested || isSaving}
            onClick={handleSaveConfiguration}
          >
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Add Configuration'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function ConfluenceItemModal({
  open,
  onClose,
  refreshIntegrations,
  metadata,
  isEditing
}: IntegrationItemModalProps) {
  const { currentWorkspace } = useWorkspaceContext()
  const [email, setEmail] = useState<string>('')
  const [url, setUrl] = useState<string>('')
  const [token, setToken] = useState<string>('')
  const [tested, setTested] = useState<boolean>(false)
  const [isTestingConnection, setIsTestingConnection] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const confluenceMetadata = metadata as {
    email: string
    server_url: string
    api_token: string
  } | null

  useEffect(() => {
    if (open && confluenceMetadata) {
      setEmail(confluenceMetadata.email || '')
      setUrl(confluenceMetadata.server_url || '')
      setToken(confluenceMetadata.api_token || '')
      if (
        confluenceMetadata.email &&
        confluenceMetadata.server_url &&
        confluenceMetadata.api_token
      ) {
        setTested(true)
      } else {
        setTested(false)
      }
      setErrorMessage('')
    }
  }, [open, confluenceMetadata])

  const handleInputChange = (field: 'email' | 'url' | 'token', value: string) => {
    if (field === 'email') setEmail(value)
    else if (field === 'url') setUrl(value)
    else setToken(value)

    if (errorMessage || tested) {
      setErrorMessage('')
      setTested(false)
    }
  }

  const handleTestConnection = async () => {
    if (!email || !url || !token) {
      setErrorMessage('Please enter Email, URL and Token')
      return
    }

    if (!currentWorkspace?.uuid) {
      setErrorMessage('Workspace not found')
      return
    }

    setIsTestingConnection(true)
    setErrorMessage('')
    try {
      const data = await fetchWrapper.post<{ success: boolean; message: string }>(
        `/api/workspace/${currentWorkspace.uuid}/integration/confluence_credentials/test`,
        {
          email,
          url,
          token
        },
        { credentials: 'include' }
      )

      if (data.success) {
        setTested(true)
        setErrorMessage('')
      } else {
        setTested(false)
        setErrorMessage(data.message || 'Connection failed. Please retry.')
      }
    } catch (error) {
      setTested(false)
      setErrorMessage('Connection failed. Please retry.')
      console.error('Test connection error:', error)
    } finally {
      setIsTestingConnection(false)
    }
  }

  const handleSaveConfiguration = async () => {
    if (!currentWorkspace?.uuid) {
      BetterToast.error('Workspace not found')
      return
    }

    setIsSaving(true)
    try {
      const method = isEditing ? 'put' : 'post'
      const data = await fetchWrapper[method]<{ success: boolean; message: string }>(
        `/api/workspace/${currentWorkspace.uuid}/integration/confluence_credentials`,
        {
          email,
          url,
          token
        },
        { credentials: 'include' }
      )

      if (data.success) {
        BetterToast.success(
          isEditing ? 'Integration updated successfully!' : 'Integration saved successfully!'
        )
        refreshIntegrations()
        onClose()
      } else {
        BetterToast.error(data.message || `Failed to ${isEditing ? 'update' : 'save'} integration`)
      }
    } catch (error) {
      BetterToast.error(`Failed to ${isEditing ? 'update' : 'save'} integration. Please try again.`)
      console.error('Save integration error:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const disabled = isTestingConnection || !email || !url || !token

  return (
    <Modal className='w-[600px]' isOpen={open} onClose={onClose} hideCloseButton={false}>
      <div className='p-4 flex gap-x-4 items-center border-b border-neutral-border-2'>
        <img
          className='w-8 h-8 select-none pointer-events-none'
          src={AtlassianSvg}
          alt='Confluence'
          draggable={false}
        />
        <Typography fontFamily='geist' fontSize='titleSm' fontWeight='bold' className='select-none'>
          Confluence
        </Typography>
      </div>
      <div className='p-12'>
        <div className='w-[360px] mx-auto flex flex-col gap-y-4'>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Email
            </Typography>
            <InputArea
              value={email}
              onChange={e => handleInputChange('email', e.target.value)}
              fullWidth
              singleLine
              placeholder='user@example.com'
            />
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Confluence URL
            </Typography>
            <InputArea
              value={url}
              onChange={e => handleInputChange('url', e.target.value)}
              fullWidth
              singleLine
              placeholder='https://your-domain.atlassian.net/wiki'
            />
            <Typography
              fontFamily='geist'
              fontSize='labelSm'
              className='text-tokens-neutral-text-tertiary select-none'
            >
              e.g. https://your-domain.atlassian.net/wiki
            </Typography>
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              API Token
            </Typography>
            <InputArea
              value={token}
              onChange={e => handleInputChange('token', e.target.value)}
              fullWidth
              singleLine
              type='password'
            />
          </div>
          <div className='flex items-center justify-end gap-x-2'>
            {errorMessage && (
              <Typography
                fontFamily='geistMono'
                fontSize='labelSm'
                className='text-tokens-status-error-text'
                fontWeight='semibold'
              >
                {errorMessage}
              </Typography>
            )}
            <Button
              variant='borderless'
              className={(() => {
                if (tested) return 'bg-tokens-neutral-fill-fill-6'
                return undefined
              })()}
              onClick={handleTestConnection}
              isDisabled={disabled}
            >
              <div className='flex gap-x-2 items-center select-none'>
                {isTestingConnection ? (
                  <>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      className='animate-spin'
                    >
                      <path
                        d='M8 1.5C6.71442 1.5 5.45772 1.88122 4.3888 2.59545C3.31988 3.30968 2.48676 4.32484 1.99479 5.51256C1.50282 6.70028 1.37409 8.00721 1.6249 9.26809C1.8757 10.529 2.49477 11.6872 3.40381 12.5962C4.31285 13.5052 5.47104 14.1243 6.73192 14.3751C7.99279 14.6259 9.29973 14.4972 10.4874 14.0052C11.6752 13.5132 12.6903 12.6801 13.4046 11.6112C14.1188 10.5423 14.5 9.28558 14.5 8'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                      />
                    </svg>
                    <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                      Testing...
                    </Typography>
                  </>
                ) : tested ? (
                  <>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M7.99961 14.4C11.5342 14.4 14.3996 11.5346 14.3996 8.00001C14.3996 4.46538 11.5342 1.60001 7.99961 1.60001C4.46499 1.60001 1.59961 4.46538 1.59961 8.00001C1.59961 11.5346 4.46499 14.4 7.99961 14.4ZM10.9653 6.96569C11.2777 6.65327 11.2777 6.14674 10.9653 5.83432C10.6529 5.5219 10.1463 5.5219 9.83392 5.83432L7.19961 8.46863L6.16529 7.43432C5.85288 7.1219 5.34634 7.1219 5.03392 7.43432C4.7215 7.74674 4.7215 8.25327 5.03392 8.56569L6.63392 10.1657C6.94634 10.4781 7.45288 10.4781 7.76529 10.1657L10.9653 6.96569Z'
                        fill='#2C8450'
                      />
                    </svg>
                    <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                      Test
                    </Typography>
                  </>
                ) : errorMessage ? (
                  <>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M7.99967 14.5953C4.31767 14.5953 1.33301 11.6107 1.33301 7.92866C1.33301 4.24733 4.31767 1.26199 7.99967 1.26199C11.6817 1.26199 14.6663 4.24733 14.6663 7.92866C14.6663 11.6107 11.6817 14.5953 7.99967 14.5953ZM7.99967 4.59533C7.82286 4.59533 7.65329 4.66556 7.52827 4.79059C7.40325 4.91561 7.33301 5.08518 7.33301 5.26199V8.59533C7.33301 8.77214 7.40325 8.94171 7.52827 9.06673C7.65329 9.19176 7.82286 9.26199 7.99967 9.26199C8.17649 9.26199 8.34605 9.19176 8.47108 9.06673C8.5961 8.94171 8.66634 8.77214 8.66634 8.59533V5.26199C8.66634 5.08518 8.5961 4.91561 8.47108 4.79059C8.34605 4.66556 8.17649 4.59533 7.99967 4.59533ZM7.99967 11.262C8.17649 11.262 8.34605 11.1918 8.47108 11.0667C8.5961 10.9417 8.66634 10.7721 8.66634 10.5953C8.66634 10.4185 8.5961 10.2489 8.47108 10.1239C8.34605 9.9989 8.17649 9.92866 7.99967 9.92866C7.82286 9.92866 7.65329 9.9989 7.52827 10.1239C7.40325 10.2489 7.33301 10.4185 7.33301 10.5953C7.33301 10.7721 7.40325 10.9417 7.52827 11.0667C7.65329 11.1918 7.82286 11.262 7.99967 11.262V11.262Z'
                        fill='#D94B36'
                      />
                    </svg>
                    <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                      Test Again
                    </Typography>
                  </>
                ) : (
                  <>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M14.5 3C14.6326 3 14.7598 3.05268 14.8536 3.14645C14.9473 3.24021 15 3.36739 15 3.5V12.5C15 12.6326 14.9473 12.7598 14.8536 12.8536C14.7598 12.9473 14.6326 13 14.5 13H1.5C1.36739 13 1.24021 12.9473 1.14645 12.8536C1.05268 12.7598 1 12.6326 1 12.5V3.5C1 3.36739 1.05268 3.24021 1.14645 3.14645C1.24021 3.05268 1.36739 3 1.5 3H14.5ZM1.5 2C1.10218 2 0.720644 2.15804 0.43934 2.43934C0.158035 2.72064 0 3.10218 0 3.5L0 12.5C0 12.8978 0.158035 13.2794 0.43934 13.5607C0.720644 13.842 1.10218 14 1.5 14H14.5C14.8978 14 15.2794 13.842 15.5607 13.5607C15.842 13.2794 16 12.8978 16 12.5V3.5C16 3.10218 15.842 2.72064 15.5607 2.43934C15.2794 2.15804 14.8978 2 14.5 2H1.5Z'
                        fill='#262626'
                        fillOpacity={disabled ? '0.2' : '1'}
                      />
                    </svg>
                    <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                      Test Connection
                    </Typography>
                  </>
                )}
              </div>
            </Button>
          </div>
        </div>
      </div>
      <div className='py-4 px-8 flex gap-2 bg-tokens-neutral-bg-grey-1 border-t border-tokens-neutral-border-2 justify-end items-center select-none'>
        <div className='flex gap-2'>
          <Button
            variant='outlined'
            onClick={onClose}
            className='text-[#262626] border-[#D6D6D6] bg-white hover:bg-gray-50'
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            isDisabled={!tested || isSaving}
            onClick={handleSaveConfiguration}
          >
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Add Configuration'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function BoomiItemModal({
  open,
  onClose,
  refreshIntegrations,
  metadata,
  isEditing
}: IntegrationItemModalProps) {
  const { currentWorkspace } = useWorkspaceContext()
  const [accountId, setAccountId] = useState<string>('')
  const [username, setUsername] = useState<string>('')
  const [apiToken, setApiToken] = useState<string>('')
  const [uiPassword, setUiPassword] = useState<string>('')
  const [tested, setTested] = useState<boolean>(false)
  const [isTestingConnection, setIsTestingConnection] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const boomiMetadata = metadata as {
    account_id: string
    username: string
    api_token: string
    has_ui_password?: boolean
  } | null

  useEffect(() => {
    if (open && boomiMetadata) {
      setAccountId(boomiMetadata.account_id || '')
      setUsername(boomiMetadata.username || '')
      setApiToken(boomiMetadata.api_token || '')
      setUiPassword('')
      if (boomiMetadata.account_id && boomiMetadata.username && boomiMetadata.api_token) {
        setTested(true)
      } else {
        setTested(false)
      }
      setErrorMessage('')
    }
  }, [open, boomiMetadata])

  const handleInputChange = (field: 'accountId' | 'username' | 'apiToken', value: string) => {
    if (field === 'accountId') setAccountId(value)
    else if (field === 'username') setUsername(value)
    else setApiToken(value)

    if (errorMessage || tested) {
      setErrorMessage('')
      setTested(false)
    }
  }

  const handleTestConnection = async () => {
    if (!accountId || !username || !apiToken) {
      setErrorMessage('Please enter Account ID, Username and API Token')
      return
    }

    if (!currentWorkspace?.uuid) {
      setErrorMessage('Workspace not found')
      return
    }

    setIsTestingConnection(true)
    setErrorMessage('')
    try {
      const data = await fetchWrapper.post<{ success: boolean; message: string }>(
        `/api/workspace/${currentWorkspace.uuid}/integration/boomi_credentials/test`,
        {
          account_id: accountId,
          username,
          token: apiToken
        },
        { credentials: 'include' }
      )

      if (data.success) {
        setTested(true)
        setErrorMessage('')
      } else {
        setTested(false)
        setErrorMessage(data.message || 'Connection failed. Please retry.')
      }
    } catch (error) {
      setTested(false)
      setErrorMessage('Connection failed. Please retry.')
      console.error('Test connection error:', error)
    } finally {
      setIsTestingConnection(false)
    }
  }

  const handleSaveConfiguration = async () => {
    if (!currentWorkspace?.uuid) {
      BetterToast.error('Workspace not found')
      return
    }

    setIsSaving(true)
    try {
      const method = isEditing ? 'put' : 'post'
      const body: Record<string, string> = {
        account_id: accountId,
        username,
        token: apiToken
      }
      if (uiPassword) {
        body.ui_password = uiPassword
      }
      const data = await fetchWrapper[method]<{ success: boolean; message: string }>(
        `/api/workspace/${currentWorkspace.uuid}/integration/boomi_credentials`,
        body,
        { credentials: 'include' }
      )

      if (data.success) {
        BetterToast.success(
          isEditing ? 'Integration updated successfully!' : 'Integration saved successfully!'
        )
        refreshIntegrations()
        onClose()
      } else {
        BetterToast.error(data.message || `Failed to ${isEditing ? 'update' : 'save'} integration`)
      }
    } catch (error) {
      BetterToast.error(`Failed to ${isEditing ? 'update' : 'save'} integration. Please try again.`)
      console.error('Save integration error:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const disabled = isTestingConnection || !accountId || !username || !apiToken

  return (
    <Modal className='w-[600px]' isOpen={open} onClose={onClose} hideCloseButton={false}>
      <div className='p-4 flex gap-x-4 items-center border-b border-neutral-border-2'>
        <img
          className='w-8 h-8 select-none pointer-events-none'
          src={BoomiSvg}
          alt='Boomi'
          draggable={false}
        />
        <Typography fontFamily='geist' fontSize='titleSm' fontWeight='bold' className='select-none'>
          Boomi
        </Typography>
      </div>
      <div className='p-12'>
        <div className='w-[360px] mx-auto flex flex-col gap-y-4'>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Account ID
            </Typography>
            <InputArea
              value={accountId}
              onChange={e => handleInputChange('accountId', e.target.value)}
              fullWidth
              singleLine
              placeholder='e.g. curietech-JJHU2R'
            />
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Username
            </Typography>
            <InputArea
              value={username}
              onChange={e => handleInputChange('username', e.target.value)}
              fullWidth
              singleLine
              placeholder='user@example.com'
            />
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              API Token
            </Typography>
            <InputArea
              value={apiToken}
              onChange={e => handleInputChange('apiToken', e.target.value)}
              fullWidth
              singleLine
              type='password'
            />
            <Typography
              fontFamily='geist'
              fontSize='labelSm'
              className='text-tokens-neutral-text-tertiary select-none'
            >
              Generate in Boomi AtomSphere &gt; Settings &gt; Account Information &gt; AtomSphere
              API Tokens
            </Typography>
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              UI Password (optional)
            </Typography>
            <InputArea
              value={uiPassword}
              onChange={e => setUiPassword(e.target.value)}
              fullWidth
              singleLine
              type='password'
              placeholder={boomiMetadata?.has_ui_password ? '••••••••' : ''}
            />
            <Typography
              fontFamily='geist'
              fontSize='labelSm'
              className='text-tokens-neutral-text-tertiary select-none'
            >
              Required for browser-based UI verification. Leave blank to skip.
            </Typography>
          </div>
          <div className='flex items-center justify-end gap-x-2'>
            {errorMessage && (
              <Typography
                fontFamily='geistMono'
                fontSize='labelSm'
                className='text-tokens-status-error-text'
                fontWeight='semibold'
              >
                {errorMessage}
              </Typography>
            )}
            <Button
              variant='borderless'
              className={tested ? 'bg-tokens-neutral-fill-fill-6' : undefined}
              onClick={handleTestConnection}
              isDisabled={disabled}
            >
              <div className='flex gap-x-2 items-center select-none'>
                {isTestingConnection ? (
                  <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                    Testing...
                  </Typography>
                ) : tested ? (
                  <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                    Tested
                  </Typography>
                ) : (
                  <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                    Test
                  </Typography>
                )}
              </div>
            </Button>
          </div>
        </div>
      </div>
      <div className='py-4 px-8 flex gap-2 bg-tokens-neutral-bg-grey-1 border-t border-tokens-neutral-border-2 justify-end items-center select-none'>
        <div className='flex gap-2'>
          <Button
            variant='outlined'
            onClick={onClose}
            className='text-[#262626] border-[#D6D6D6] bg-white hover:bg-gray-50'
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            isDisabled={!tested || isSaving}
            onClick={handleSaveConfiguration}
          >
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Add Configuration'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function AzureDeployItemModal({
  open,
  onClose,
  refreshIntegrations,
  metadata,
  isEditing
}: IntegrationItemModalProps) {
  const { currentWorkspace } = useWorkspaceContext()
  const [clientId, setClientId] = useState<string>('')
  const [clientSecret, setClientSecret] = useState<string>('')
  const [tenantId, setTenantId] = useState<string>('')
  const [subscriptionId, setSubscriptionId] = useState<string>('')
  const [resourceGroup, setResourceGroup] = useState<string>('')
  const [location, setLocation] = useState<string>('')
  const [storageAccount, setStorageAccount] = useState<string>('')
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const azureMetadata = metadata as {
    client_id?: string
    tenant_id?: string
    subscription_id?: string
    resource_group?: string
    location?: string
    storage_account?: string
  } | null

  useEffect(() => {
    if (open) {
      setClientId(azureMetadata?.client_id || '')
      setTenantId(azureMetadata?.tenant_id || '')
      setSubscriptionId(azureMetadata?.subscription_id || '')
      setResourceGroup(azureMetadata?.resource_group || '')
      setLocation(azureMetadata?.location || '')
      setStorageAccount(azureMetadata?.storage_account || '')
      // client_secret is never returned by the API; require re-entry on edit.
      setClientSecret('')
      setErrorMessage('')
    }
  }, [open, azureMetadata])

  const handleSave = async () => {
    if (!clientId || !clientSecret || !tenantId || !subscriptionId) {
      setErrorMessage('All fields are required')
      return
    }
    if (!currentWorkspace?.uuid) {
      BetterToast.error('Workspace not found')
      return
    }

    setIsSaving(true)
    try {
      // The backend POST upserts, so we always POST (no separate PUT path).
      const data = await fetchWrapper.post<{ success: boolean; message: string }>(
        `/api/workspace/${currentWorkspace.uuid}/integration/azure_deploy_sp`,
        {
          client_id: clientId,
          client_secret: clientSecret,
          tenant_id: tenantId,
          subscription_id: subscriptionId,
          resource_group: resourceGroup,
          location: location,
          storage_account: storageAccount
        },
        { credentials: 'include' }
      )

      if (data.success) {
        BetterToast.success(
          isEditing ? 'Integration updated successfully!' : 'Integration saved successfully!'
        )
        refreshIntegrations()
        onClose()
      } else {
        BetterToast.error(data.message || `Failed to ${isEditing ? 'update' : 'save'} integration`)
      }
    } catch (error) {
      BetterToast.error(`Failed to ${isEditing ? 'update' : 'save'} integration. Please try again.`)
      console.error('Save Azure deploy integration error:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const disabled = isSaving || !clientId || !clientSecret || !tenantId || !subscriptionId

  return (
    <Modal className='w-[600px]' isOpen={open} onClose={onClose} hideCloseButton={false}>
      <div className='p-4 flex gap-x-4 items-center border-b border-neutral-border-2'>
        <img
          className='w-8 h-8 select-none pointer-events-none'
          src={AzureSvg}
          alt='Azure'
          draggable={false}
        />
        <Typography fontFamily='geist' fontSize='titleSm' fontWeight='bold' className='select-none'>
          Azure
        </Typography>
        <BetaPill />
      </div>
      <div className='p-12'>
        <div className='w-[360px] mx-auto flex flex-col gap-y-4'>
          <Typography
            fontFamily='geist'
            fontSize='labelSm'
            className='text-tokens-neutral-text-tertiary select-none'
          >
            Service principal used to deploy generated Azure Logic Apps. Create one with{' '}
            <code>az ad sp create-for-rbac --role Contributor</code> scoped to the target
            subscription.
          </Typography>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Client ID
            </Typography>
            <InputArea
              value={clientId}
              onChange={e => setClientId(e.target.value)}
              fullWidth
              singleLine
              placeholder='appId (GUID)'
            />
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Client Secret
            </Typography>
            <InputArea
              value={clientSecret}
              onChange={e => setClientSecret(e.target.value)}
              fullWidth
              singleLine
              type='password'
            />
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Tenant ID
            </Typography>
            <InputArea
              value={tenantId}
              onChange={e => setTenantId(e.target.value)}
              fullWidth
              singleLine
              placeholder='tenant (GUID)'
            />
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Subscription ID
            </Typography>
            <InputArea
              value={subscriptionId}
              onChange={e => setSubscriptionId(e.target.value)}
              fullWidth
              singleLine
              placeholder='subscription (GUID)'
            />
          </div>
          <div className='mt-2 border-t border-tokens-neutral-border-2 pt-4'>
            <Typography
              fontFamily='geist'
              fontSize='labelSm'
              className='text-tokens-neutral-text-tertiary select-none'
            >
              Deploy defaults (optional) — the agent uses these when set and prompts you for
              anything left blank.
            </Typography>
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Resource group
            </Typography>
            <InputArea
              value={resourceGroup}
              onChange={e => setResourceGroup(e.target.value)}
              fullWidth
              singleLine
              placeholder='e.g. curie-rg'
            />
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Location
            </Typography>
            <InputArea
              value={location}
              onChange={e => setLocation(e.target.value)}
              fullWidth
              singleLine
              placeholder='e.g. eastus'
            />
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Storage account
            </Typography>
            <InputArea
              value={storageAccount}
              onChange={e => setStorageAccount(e.target.value)}
              fullWidth
              singleLine
              placeholder='e.g. curiestg001'
            />
          </div>
          {errorMessage && (
            <Typography
              fontFamily='geistMono'
              fontSize='labelSm'
              className='text-tokens-status-error-text'
              fontWeight='semibold'
            >
              {errorMessage}
            </Typography>
          )}
        </div>
      </div>
      <div className='py-4 px-8 flex gap-2 bg-tokens-neutral-bg-grey-1 border-t border-tokens-neutral-border-2 justify-end items-center select-none'>
        <div className='flex gap-2'>
          <Button
            variant='outlined'
            onClick={onClose}
            className='text-[#262626] border-[#D6D6D6] bg-white hover:bg-gray-50'
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button variant='primary' isDisabled={disabled} onClick={handleSave}>
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Add Configuration'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

interface Integration<T = unknown> {
  conn_type: string
  connected: boolean
  metadata: T
}

const CURIETECH_EMAIL_DOMAINS = ['@curietech.ai', '@curietech.net', '@curietech.com']

function isCurieTechEmail(email: string | undefined | null): boolean {
  if (!email) return false
  const lower = email.toLowerCase()
  return CURIETECH_EMAIL_DOMAINS.some(domain => lower.endsWith(domain))
}

const IntegrationItemComponentMetadata = {
  anypoint_exchange: {
    label: 'Exchange',
    image: MulesoftPng,
    IntegrationItemModal: AnypointExchangeItemModal
  },
  jira_credentials: {
    label: 'Jira',
    image: AtlassianSvg,
    IntegrationItemModal: JiraItemModal
  },
  confluence_credentials: {
    label: 'Confluence',
    image: AtlassianSvg,
    IntegrationItemModal: ConfluenceItemModal
  },
  boomi_credentials: {
    label: 'Boomi',
    image: BoomiSvg,
    IntegrationItemModal: BoomiItemModal
  },
  azure_deploy_sp: {
    label: 'Azure',
    image: AzureSvg,
    beta: true,
    IntegrationItemModal: AzureDeployItemModal
  }
} as Record<
  string,
  {
    label: string
    image: string
    beta?: boolean
    IntegrationItemModal?: (props: IntegrationItemModalProps) => React.ReactNode
  }
>

function IntegrationsSettingsTab() {
  const { currentWorkspace } = useWorkspaceContext()
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { hasLicense } = useBillingContext()
  const { user } = useUserContext()

  // Check if user has access to Anypoint Exchange based on stacked licenses
  const hasAnypointExchangeAccess =
    hasLicense(AccountType.TEAM) ||
    hasLicense(AccountType.ENTERPRISE) ||
    hasLicense(AccountType.ENTERPRISE_MIGRATE) ||
    hasLicense(AccountType.ENTERPRISE_MIGRATE_ASSESS) ||
    hasLicense(AccountType.ENTERPRISE_MIGRATE_ACCELERATE) ||
    hasLicense(AccountType.ENTERPRISE_MIGRATE_REFACTOR)

  const hasBoomiAccess = isCurieTechEmail(user?.email)

  const refreshIntegrations = useCallback(
    (silent = false) => {
      if (!currentWorkspace?.uuid) {
        return
      }

      if (!silent) {
        setIsLoading(true)
      }

      return fetchWrapper
        .get<Integration[]>(`/api/workspace/${currentWorkspace.uuid}/integration`, {
          credentials: 'include'
        })
        .then(setIntegrations)
        .finally(() => {
          if (!silent) {
            setIsLoading(false)
          }
        })
    },
    [currentWorkspace?.uuid]
  )

  const refreshIntegrationsWithLoading = useCallback(() => {
    void refreshIntegrations(false)
  }, [refreshIntegrations])

  useEffect(() => {
    void refreshIntegrations(false)
  }, [refreshIntegrations])

  return (
    <>
      <div className='grid grid-cols-1 xl:grid-cols-2 gap-x-6 gap-y-12 max-w-[824px] min-w-[320px]'>
        {integrations.map(integration => {
          // Check account type for anypoint_exchange
          if (integration.conn_type === 'anypoint_exchange' && !hasAnypointExchangeAccess) {
            return null
          }

          if (integration.conn_type === 'jira_credentials' && !hasAnypointExchangeAccess) {
            return null
          }

          if (integration.conn_type === 'confluence_credentials' && !hasAnypointExchangeAccess) {
            return null
          }

          if (integration.conn_type === 'boomi_credentials' && !hasBoomiAccess) {
            return null
          }

          const integrationItemMetadata = IntegrationItemComponentMetadata[integration.conn_type]
          if (!integrationItemMetadata) {
            return null
          }

          const { label, image, beta, IntegrationItemModal } = integrationItemMetadata

          if (!IntegrationItemModal) {
            return null
          }

          return (
            <IntegrationItem
              key={integration.conn_type}
              label={label}
              image={image}
              beta={beta}
              connected={integration.connected}
              metadata={integration.metadata}
              IntegrationItemModal={IntegrationItemModal}
              refreshIntegrations={refreshIntegrationsWithLoading}
            />
          )
        })}
      </div>
    </>
  )
}

// Hook to check if there are any visible integrations
export function useHasVisibleIntegrations(): boolean {
  const { hasLicense } = useBillingContext()
  const { user } = useUserContext()

  return (
    hasLicense(AccountType.TEAM) ||
    hasLicense(AccountType.ENTERPRISE) ||
    hasLicense(AccountType.ENTERPRISE_MIGRATE) ||
    hasLicense(AccountType.ENTERPRISE_MIGRATE_ASSESS) ||
    hasLicense(AccountType.ENTERPRISE_MIGRATE_ACCELERATE) ||
    hasLicense(AccountType.ENTERPRISE_MIGRATE_REFACTOR) ||
    isCurieTechEmail(user?.email)
  )
}

export default IntegrationsSettingsTab
